import React, { useRef, useState, useCallback, useEffect } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-playlist';
import 'videojs-playlist-ui';
import 'videojs-playlist-ui/dist/videojs-playlist-ui.css';
import SeriesPlayerView from './SeriesPlayerView';
import FeaturedAudioView from './FeaturedAudioView';
import BroadcastPlayerView from './BroadcastPlayerView';
import { useTextExpansion } from '../contexts/TextExpansionContext';
import AudioAdUI from './AudioAdUI';
import AdCountdownUI from './AdCountdownUI';
import { getTranslations } from '../utils/translations';
import { stripHtml } from '../utils/htmlUtils';
import * as analytics from '../utils/playerAnalytics';

// Our custom AdCountdownUI overlay owns the skip button (the VAST no longer sets a
// skipoffset, so IMA renders no native skip button). This is the number of seconds
// the viewer must watch before our "Omitir anuncio" button becomes clickable.
const CUSTOM_AD_SKIP_OFFSET_SECONDS = 15;

const NewVideoPlayer = ({ playlist, onPlaylistItemChange, currentPlaylistItem, isSeries = false, prerollAdEnabled = false, postrollAdEnabled = true, pauseAdUrl = null, pauseAdClickUrl = null, initialIndex = null, textColor }) => {
    console.log('prerollAdEnabled:', prerollAdEnabled);
    console.log('postrollAdEnabled:', postrollAdEnabled);
    const t = getTranslations();
    const playerRef = useRef(null);
    const adContainerRef = useRef(null);
    const adTimerIntervalRef = useRef(null); // For fallback timer
    const adsLoaderRef = useRef(null);
    const adsManagerRef = useRef(null);
    const adDisplayContainerRef = useRef(null);
    const adContainerInitializedRef = useRef(false);
    const upcomingItemRef = useRef(null);
    const playlistContainerRef = useRef(null);
    const [isAdPlaying, setIsAdPlaying] = useState(false);
    const [isAdRequested, setIsAdRequested] = useState(false); // Ad requested but not yet LOADED
    const isAdRequestedRef = useRef(false); // Ref mirror to avoid player re-init from useCallback deps
    const [isAudioAd, setIsAudioAd] = useState(false);
    const [posterDimensions, setPosterDimensions] = useState(null);
    const [adRemainingTime, setAdRemainingTime] = useState(null);
    const [adDuration, setAdDuration] = useState(null);
    const [adSkipTimeOffset, setAdSkipTimeOffset] = useState(null);
    const [isAdMuted, setIsAdMuted] = useState(false);
    const [adCompanionPosterUrl, setAdCompanionPosterUrl] = useState(null);
    const [adCompanionClickUrl, setAdCompanionClickUrl] = useState(null);
    const [adCompanionAltText, setAdCompanionAltText] = useState(null);
    const [isPostroll, setIsPostroll] = useState(false);
    const isPostrollRef = useRef(false); // Ref to track postroll in closures
    const [isAdPaused, setIsAdPaused] = useState(false); // Track when ad is paused (e.g. after click-through)
    const [isContentPaused, setIsContentPaused] = useState(false); // Track when main video is paused
    const playingAudioAdRef = useRef(false); // Ref to track when we're playing audio ad through Video.js
    const nextItemAfterPostrollRef = useRef(null); // Store the next item index when postroll starts
    const manualPlaylistClickRef = useRef(false); // Track when user manually clicks a playlist item
    const isAdPlayingRef = useRef(false); // Ref to mirror isAdPlaying for event handlers
    const { isTextExpanded, handleTextExpansion } = useTextExpansion();
    const isInitialPlaylistItemSetRef = useRef(true); // NEW: Track initial playlist item setting
    const currentAdTagUrlRef = useRef(null); // Store current ad tag URL for manual fallback
    const blockPostrollRef = useRef(true); // Block postroll triggers - start as TRUE to block VMAP postroll during preroll
    const contentHasPlayedRef = useRef(false); // Track if content has actually played (to allow postroll)
    const isVmapRequestRef = useRef(false); // Track if current ad request is a VMAP (contains multiple ad breaks)
    const contentStartTimeRef = useRef(0); // Track when content started playing to prevent premature postrolls
    // Analytics refs
    const firstPlayFiredRef = useRef(false); // Distinguish first trackPlay vs subsequent trackResume
    const seekFromRef = useRef(0); // Captures pre-seek position so trackSeek has a `from` value (seeked fires post-jump)
    const milestonesFiredRef = useRef(new Set()); // Which of 25/50/75 have fired for the current episode
    const previousPlaylistIndexRef = useRef(null); // Previous playlist index, used to decide next vs prev on transitions
    const previousPlaylistItemRef = useRef(null); // Previous playlist item for next/prev payloads. Separate from upcomingItemRef because beforeplaylistitem updates that ref before playlistitem fires.
    const currentAudioAdRef = useRef(null); // Active audio-ad IMA object, needed for AudioAdUI click callback
    const seekDebounceRef = useRef(null); // setTimeout id for collapsing rapid seeked events during a scrub drag
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 600);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const cleanupAdsManager = () => {
        if (adsManagerRef.current) {
            try {
                adsManagerRef.current.destroy();
            } catch (e) {
                // ignore
            }
            adsManagerRef.current = null;
        }
        if (adTimerIntervalRef.current) {
            clearInterval(adTimerIntervalRef.current);
            adTimerIntervalRef.current = null;
        }
        
        // Reset all ad state
        setIsAdPlaying(false);
        isAdPlayingRef.current = false;
        setIsAdRequested(false);
        isAdRequestedRef.current = false;
        setIsAudioAd(false);
        setIsAdPaused(false);
        setAdRemainingTime(null);
        setAdDuration(null);
        setIsAdMuted(null);
        setAdCompanionPosterUrl(null);
        setAdCompanionClickUrl(null);
        setAdCompanionAltText(null);
    };

    const onAdError = (adErrorEvent) => {
        const error = adErrorEvent.getError();
        console.error('IMA: Ad Error:', error.toString());
        console.error('IMA: Ad Error Code:', error.getErrorCode());
        console.error('IMA: Ad Error Message:', error.getMessage());
        console.error('IMA: Full Ad Error Event:', adErrorEvent);
        console.error('IMA: Current Ad Tag URL:', currentAdTagUrlRef.current);
        
        cleanupAdsManager(); // This now resets all flags

        // If this was a postroll error, we need to clean up postroll state and advance
        if (isPostrollRef.current) {
            console.log('⚠️ Postroll error, cleaning up and advancing');
            setIsPostroll(false);
            isPostrollRef.current = false;
            
            const nextIndex = nextItemAfterPostrollRef.current;
            nextItemAfterPostrollRef.current = null;
            
            if (nextIndex !== null && playerRef.current) {
                // Restore autoadvance
                playerRef.current.playlist.autoadvance(isSeries ? 1 : 0);
                // Advance to next item
                playerRef.current.playlist.currentItem(nextIndex);
            }
        }

        // Safely play the content, handling potential interruptions on mobile
        if (playerRef.current && !playerRef.current.isDisposed() && playerRef.current.paused()) {
            const playPromise = playerRef.current.play();
            if (playPromise !== undefined) {
                playPromise.then(() => {
                    // Playback started successfully.
                }).catch(error => {
                    // Autoplay was prevented.
                    console.warn('Playback was interrupted or prevented on ad error:', error);
                });
            }
        }
    };

    const handlePlayerMount = useCallback((videoNode) => {
        if (!videoNode || playerRef.current) {
            return;
        }

        // Add playsinline attribute for iOS compatibility
        videoNode.setAttribute('playsinline', 'true');

        const lang = window.broadcastPlayerData?.language || 'en-US';
        const videoJsLang = lang.toLowerCase().startsWith('es') ? 'es' : 'en';

        // Add Spanish translations to Video.js if using Spanish
        if (videoJsLang === 'es') {
            videojs.addLanguage('es', {
                "Play": "Reproducir",
                "Play Video": "Reproducir video",
                "Pause": "Pausa",
                "Current Time": "Tiempo actual",
                "Duration": "Duración total",
                "Remaining Time": "Tiempo restante",
                "Stream Type": "Tipo de transmisión",
                "LIVE": "EN DIRECTO",
                "Loaded": "Cargado",
                "Progress": "Progreso",
                "Fullscreen": "Pantalla completa",
                "Non-Fullscreen": "Pantalla normal",
                "Mute": "Silenciar",
                "Unmute": "Activar sonido",
                "Playback Rate": "Velocidad de reproducción",
                "Subtitles": "Subtítulos",
                "subtitles off": "Subtítulos desactivados",
                "Captions": "Subtítulos especiales",
                "captions off": "Subtítulos especiales desactivados",
                "Chapters": "Capítulos",
                "You aborted the media playback": "Ha interrumpido la reproducción del contenido.",
                "A network error caused the media download to fail part-way.": "Un error de red ha provocado un fallo en la descarga del contenido.",
                "The media could not be loaded, either because the server or network failed or because the format is not supported.": "No se ha podido cargar el contenido debido a un fallo de red o del servidor, o porque el formato no es compatible.",
                "The media playback was aborted due to a corruption problem or because the media used features your browser did not support.": "La reproducción se ha interrumpido por un problema de corrupción de datos o porque el contenido usa funciones que su navegador no admite.",
                "No compatible source was found for this media.": "No se ha encontrado ninguna fuente compatible con este contenido."
            });
        }

        const player = videojs(videoNode, {
            fluid: true,
            aspectRatio: '16:9',
            controls: true,
            language: videoJsLang,
            autoplay: isSeries ? false : true,
            preload: 'auto',
            inactivityTimeout: 0,
            controlBar: {
                playToggle: true,
                volumePanel: {
                    inline: true,
                    volumeControl: true
                },
                currentTimeDisplay: false,
                timeDivider: false,
                durationDisplay: false,
                progressControl: true,
                remainingTimeDisplay: true,
                customControlSpacer: true,
                playbackRateMenuButton: false,
                chaptersButton: false,
                descriptionsButton: false,
                subsCapsButton: false,
                audioTrackButton: false,
                pictureInPictureToggle: false,
                fullscreenToggle: true,
                liveDisplay: false,
                seekToLive: false
            }
        });
        playerRef.current = player;

        player.on('ads-ad-started', () => {
            player.controls(false);
        });

        player.on('ads-ad-ended', () => {
            player.controls(true);
        });

        player.on('ended', () => {
            const player = playerRef.current;
            if (player && !player.isDisposed()) {
                // Prevent triggering postroll if we are already playing an ad (especially manual audio ad)
                if (playingAudioAdRef.current || isAdPlayingRef.current) {
                    console.log('🛑 Global ended event ignored: Ad already active');
                    return;
                }

                // Add a small delay to ensure the playlist plugin has updated its internal index
                setTimeout(() => {
                    const playlist = player.playlist();
                    const currentItemIndex = player.playlist.currentItem();
                    const currentItem = playlist[currentItemIndex];
                    console.log('ended event triggered (delayed). Current item index:', currentItemIndex, 'item:', currentItem?.name);
                    
                    // IGNORE ended event if it happens at the very beginning (spurious event)
                    const currentTime = player.currentTime();
                    const duration = player.duration();
                    
                    if (!duration || isNaN(duration) || duration <= 0) {
                         console.log(`🛑 Global ended event ignored: Invalid duration (${duration})`);
                         return;
                    }

                    // Also ignore if we are not actually near the end (e.g. some weird seek/load event)
                    const timeRemaining = duration - currentTime;
                    if (timeRemaining > 2 && currentTime < duration - 2) {
                         console.log(`🛑 Global ended event ignored: Not at end. Current: ${currentTime}, Duration: ${duration}, Remaining: ${timeRemaining}`);
                         return;
                    }

                    if (currentTime < 2) {
                         console.log(`🛑 Global ended event ignored: Premature ended event at start ${currentTime}s / ${duration}s`);
                         return;
                    }

                    analytics.trackComplete(currentItem, duration);

                    if (currentItem && currentItem.postrollAdUrl && postrollAdEnabled) {
                        console.log('✅ postrollAdUrl found, requesting ads:', currentItem.postrollAdUrl);
                        
                        // Explicitly unblock postroll if we have a valid URL and are at the end
                        blockPostrollRef.current = false;
                        
                        setIsPostroll(true);
                        isPostrollRef.current = true; // Update ref for closures
                        // Store the next item index for when we need to skip or finish the postroll
                        nextItemAfterPostrollRef.current = currentItemIndex + 1;
                        console.log('📍 Next item after postroll will be index:', nextItemAfterPostrollRef.current);
                        player.playlist.autoadvance(0); // Disable autoadvance
                        requestAds(currentItem.postrollAdUrl);
                    } else {
                        console.log('ℹ️ No postroll ad URL or postroll disabled for this item');
                    }
                }, 100);
            }
        });


        player.playlist(playlist);
        player.playlist.autoadvance(isSeries ? 1 : 0);
        
        // Handle initial index if provided, otherwise default logic
        if (initialIndex !== null && initialIndex >= 0 && initialIndex < playlist.length) {
            player.playlist.currentItem(initialIndex);
        } else if (!isSeries && playlist.length > 0) {
            // For broadcast player, start at the last playlist item (newest episode)
            player.playlist.currentItem(playlist.length - 1);
        }
        
        // DO NOT initialize playlist UI here. It will be handled in a useEffect.

        console.log('🔍 IMA SDK Check - isSeries:', isSeries);
        console.log('🔍 IMA SDK Check - hasGoogle:', !!window.google);
        console.log('🔍 IMA SDK Check - hasIMA:', !!(window.google && window.ima));

    let adDisplayContainer;
        if (window.google && window.google.ima) {
            // iOS-specific setting for compatibility
            window.google.ima.settings.setDisableCustomPlaybackForIOS10Plus(true);
            console.log('🍏 iOS custom playback disabled for IMA');

            console.log('🎯 IMA SDK detected, initializing ad components');
            // Pass the videoNode as the click element
            adDisplayContainer = new window.google.ima.AdDisplayContainer(adContainerRef.current, videoNode, videoNode);
            adDisplayContainerRef.current = adDisplayContainer;
            const adsLoader = new window.google.ima.AdsLoader(adDisplayContainer);
            adsLoaderRef.current = adsLoader;
            console.log('✅ AdDisplayContainer and AdsLoader created');

            const onAdsManagerLoaded = (adsManagerLoadedEvent) => {
                // Configure rendering settings to disable IMA SDK's built-in UI
                // (we use our own custom skip/countdown UI)
                const adsRenderingSettings = new window.google.ima.AdsRenderingSettings();
                adsRenderingSettings.uiElements = []; // Remove all built-in UI (skip button, countdown, etc.)
                
                const adsManager = adsManagerLoadedEvent.getAdsManager(videoNode, adsRenderingSettings);
                adsManagerRef.current = adsManager;
                console.log('IMA: AdsManagerLoadedEvent:', adsManagerLoadedEvent); // Added debug

                const isIPhone = /iPhone/i.test(navigator.userAgent || '');

                adsManager.addEventListener(window.google.ima.AdEvent.Type.LOADED, (adEvent) => {
                    const adData = adEvent.getAdData();
                    const ad = adEvent.getAd();
                    console.log('Ad Data', adData);            
                    console.log('IMA: Full Ad Object:', ad); // Added debug
                    console.log('🎬 IMA Ad LOADED event fired');
                    console.log('📦 Ad contentType:', adData.contentType);
                    console.log('📦 Ad duration:', adData.duration);
                    
                    // Check if this is a postroll being triggered too early (VMAP postroll during preroll-to-content transition)
                    // The adPodInfo.timeOffset will be -1 for postroll ads, 0 for preroll
                    const adPodInfo = ad.getAdPodInfo();
                    const timeOffset = adPodInfo ? adPodInfo.getTimeOffset() : 0;
                    console.log('📦 Ad timeOffset:', timeOffset, '(postroll = -1, preroll = 0), blockPostrollRef:', blockPostrollRef.current, 'contentHasPlayed:', contentHasPlayedRef.current, 'isPostrollRef:', isPostrollRef.current);
                    
                    // Check if this is a postroll ad (timeOffset === -1) and if we should block it
                    // We only block if content hasn't played yet OR if it hasn't played *enough* (VMAP premature trigger)
                    if (timeOffset === -1 && !isPostrollRef.current) {
                        const timeSinceStart = Date.now() - contentStartTimeRef.current;
                        const isPremature = timeSinceStart < 5000; // 5 seconds buffer
                        
                        console.log(`🛡️ Postroll check: timeSinceStart=${timeSinceStart}ms, isPremature=${isPremature}, contentHasPlayed=${contentHasPlayedRef.current}`);

                        if (!contentHasPlayedRef.current || isPremature) {
                            console.log('🛡️ BLOCKING premature postroll ad');
                            // Skip this ad by stopping the adsManager for this ad
                            if (adsManagerRef.current) {
                                try {
                                    adsManagerRef.current.discardAdBreak();
                                } catch (e2) {
                                    console.warn('⚠️ Could not discard ad break:', e2);
                                }
                            }
                            return; // Don't process this ad
                        }
                    }
                    
                    // If this is a preroll ad (timeOffset === 0) but we're in postroll mode,
                    // skip this ad - VMAP is sending preroll when we only want postroll
                    // ONLY apply this blocking logic for VMAP requests - direct VAST files always have timeOffset 0
                    if (timeOffset === 0 && isPostrollRef.current && isVmapRequestRef.current) {
                        console.log('🛡️ BLOCKING preroll ad during VMAP postroll request - skipping to postroll');
                        // Skip this preroll ad
                        if (adsManagerRef.current) {
                            try {
                                adsManagerRef.current.skip();
                            } catch (e) {
                                console.warn('⚠️ Could not skip preroll ad:', e);
                                try {
                                    adsManagerRef.current.discardAdBreak();
                                } catch (e2) {
                                    console.warn('⚠️ Could not discard ad break:', e2);
                                }
                            }
                        }
                        return; // Don't process this ad
                    }



                    setAdDuration(adData.duration); // Store the actual ad duration

                    // IMA's skip offset — still read for the manual AUDIO ad path below,
                    // whose own VAST may set a skipoffset and which runs its own countdown.
                    const skipTimeOffset = typeof ad.getSkipTimeOffset === 'function' ? ad.getSkipTimeOffset() : -1;
                    console.log('📦 Ad skipTimeOffset (IMA):', skipTimeOffset);

                    const isAudioAd = adData.contentType === 'audio/mp3' || adData.contentType === 'audio/mpeg';

                    // Seed the skip countdown. Video ads: our overlay owns skip (the VAST has no
                    // skipoffset, so IMA renders no native button) — seed our own gate, capped to the
                    // ad length. Audio ads: keep IMA's offset; their manual path updates it on timeupdate.
                    setAdSkipTimeOffset(isAudioAd
                        ? skipTimeOffset
                        : Math.min(CUSTOM_AD_SKIP_OFFSET_SECONDS, adData.duration || CUSTOM_AD_SKIP_OFFSET_SECONDS));
                    
                    if (isAudioAd) {
                        console.log('🎵 Audio ad detected - setting isAudioAd to TRUE');
                        console.log('🎵 Full ad object:', ad);
                        console.log('🎵 Ad methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(ad)));
                        setIsAudioAd(true);
                        currentAudioAdRef.current = ad; // Analytics: needed by AudioAdUI.onAdClick and the manual complete/skip hooks
                        
                        // For audio ads, we need to extract the media URL and play it through Video.js
                        // because IMA SDK doesn't properly handle audio-only ads
                        try {
                            const mediaUrl = ad.getMediaUrl();
                            console.log('🎵 Audio ad media URL:', mediaUrl);
                            
                            // FIRST: Extract companion ad data BEFORE stopping adsManager
                            let companionUrl = null;
                            let companionClick = null;
                            try {
                                if (ad && typeof ad.getCompanionAds === 'function') {
                                    // Use looser settings to find the companion (MIME types might be mismatched in VAST)
                                    const settings = new window.google.ima.CompanionAdSelectionSettings();
                                    settings.resourceType = window.google.ima.CompanionAdSelectionSettings.ResourceType.ALL;
                                    settings.creativeType = window.google.ima.CompanionAdSelectionSettings.CreativeType.ALL;
                                    settings.sizeCriteria = window.google.ima.CompanionAdSelectionSettings.SizeCriteria.IGNORE;

                                    console.log('📦 Attempting to get companion ads with loose settings (ALL/ALL/IGNORE)...');
                                    let companions = ad.getCompanionAds(0, 0, settings);
                                    
                                    if (!companions || companions.length === 0) {
                                        console.log('📦 No companions found with 0x0, trying 1920x1080...');
                                        companions = ad.getCompanionAds(1920, 1080, settings);
                                    }

                                    console.log('📦 Companion ads found:', companions);
                                    
                                    if (Array.isArray(companions)) {
                                        for (const c of companions) {
                                            // Log the companion object keys to understand what we're working with
                                            console.log('📦 Companion object keys:', Object.keys(Object.getPrototypeOf(c)));
                                            
                                            let urlCandidate = null;
                                            // Check all possible property names for the URL
                                            if (typeof c.getResourceValue === 'function') urlCandidate = c.getResourceValue();
                                            else if (typeof c.getContent === 'function') urlCandidate = c.getContent();
                                            else if (c.resourceUrl) urlCandidate = c.resourceUrl;
                                            else if (c.staticUrl) urlCandidate = c.staticUrl;
                                            else if (c.url) urlCandidate = c.url;
                                            
                                            if (urlCandidate) {
                                                const urlStr = String(urlCandidate);
                                                console.log('🎵 Companion urlCandidate:', urlStr);
                                                if (urlStr.startsWith('http') || urlStr.startsWith('//')) {
                                                    companionUrl = urlStr;
                                                } else if (urlStr.trim().startsWith('<')) {
                                                    const srcMatch = urlStr.match(/src=["'](.*?)["']/);
                                                    if (srcMatch && srcMatch[1]) {
                                                        companionUrl = srcMatch[1];
                                                    }
                                                    const hrefMatch = urlStr.match(/href=["'](.*?)["']/);
                                                    if (hrefMatch && hrefMatch[1]) {
                                                        companionClick = hrefMatch[1];
                                                    }
                                                }

                                                if (companionUrl) {
                                                    let clickCandidate = null;
                                                    if (typeof c.getClickThroughUrl === 'function') clickCandidate = c.getClickThroughUrl();
                                                    else if (typeof c.getClickThroughURL === 'function') clickCandidate = c.getClickThroughURL();
                                                    else if (typeof c.getCompanionClickThroughUrl === 'function') clickCandidate = c.getCompanionClickThroughUrl();
                                                    else if (c.clickThroughUrl) clickCandidate = c.clickThroughUrl;
                                                    else if (c.clickThroughURL) clickCandidate = c.clickThroughURL;
                                                    else if (c.companionClickThroughUrl) clickCandidate = c.companionClickThroughUrl;
                                                    
                                                    if (clickCandidate && (String(clickCandidate).startsWith('https') || String(clickCandidate).startsWith('//'))) {
                                                        companionClick = String(clickCandidate);
                                                    }
                                                    break; // Stop after finding the first valid image companion
                                                }
                                            }
                                        }
                                    }
                                }
                                
                                if (companionUrl) {
                                    console.log('🖼️ Companion image (audio ad):', companionUrl);
                                    setAdCompanionPosterUrl(companionUrl);
                                    if (companionClick) {
                                        console.log('🔗 Companion click URL:', companionClick);
                                        setAdCompanionClickUrl(companionClick);
                                    }
                                } else {
                                    console.log('⚠️ No valid companion URL extracted from IMA SDK. Will fetch VAST manually...');
                                }
                            } catch (companionErr) {
                                console.warn('⚠️ Error reading companion ad data:', companionErr);
                            }
                            
                            // Helper function to fetch companion poster from VAST manually
                            const fetchCompanionPoster = async () => {
                                if (companionUrl) {
                                    // Already have companion URL from IMA SDK
                                    return { posterUrl: companionUrl, clickUrl: companionClick, altText: null };
                                }
                                
                                const vastUrl = currentAdTagUrlRef.current;
                                if (!vastUrl) {
                                    return { posterUrl: null, clickUrl: null, altText: null };
                                }
                                
                                console.log('⚠️ Fetching VAST manually from:', vastUrl);

                                // Helper to parse XML response
                                const parseVastXml = (xmlString) => {
                                    const parser = new DOMParser();
                                    const xmlDoc = parser.parseFromString(xmlString, "text/xml");
                                    
                                    // Find Companion element with StaticResource
                                    const companions = xmlDoc.getElementsByTagName('Companion');
                                    let foundPoster = null;
                                    let foundClick = null;
                                    let foundAltText = null;
                                    
                                    for (let i = 0; i < companions.length; i++) {
                                        const companion = companions[i];
                                        const staticResource = companion.getElementsByTagName('StaticResource')[0];
                                        if (staticResource) {
                                            const url = staticResource.textContent.trim();
                                            if (url) {
                                                foundPoster = url;
                                                const clickThrough = companion.getElementsByTagName('CompanionClickThrough')[0];
                                                if (clickThrough) {
                                                    foundClick = clickThrough.textContent.trim();
                                                }
                                                const altTextEl = companion.getElementsByTagName('AltText')[0];
                                                if (altTextEl) {
                                                    foundAltText = altTextEl.textContent.trim();
                                                }
                                                break;
                                            }
                                        }
                                    }
                                    return { foundPoster, foundClick, foundAltText };
                                };

                                // TRY DIRECT FETCH FIRST
                                try {
                                    console.log('⚠️ Attempting direct fetch of VAST URL...');
                                    const directResponse = await fetch(vastUrl);
                                    if (directResponse.ok) {
                                        const text = await directResponse.text();
                                        console.log('✅ Direct fetch successful, parsing XML...');
                                        const { foundPoster, foundClick, foundAltText } = parseVastXml(text);
                                        
                                        if (foundPoster) {
                                            console.log('🖼️ Manually parsed companion poster (Direct):', foundPoster);
                                            return { posterUrl: foundPoster, clickUrl: foundClick, altText: foundAltText };
                                        } else {
                                            console.warn('⚠️ Could not find StaticResource in direct VAST.');
                                        }
                                    } else {
                                        console.warn(`⚠️ Direct fetch failed with status: ${directResponse.status}`);
                                    }
                                } catch (directErr) {
                                    console.warn('⚠️ Direct fetch failed (likely CORS), falling back to proxy:', directErr);
                                }
                                
                                // FALLBACK TO PROXY
                                try {
                                    const restUrl = window.broadcastPlayerData?.restUrl || '/wp-json/wp/v2/';
                                    const proxyEndpoint = `${restUrl}proxy-rss`;
                                    const nonce = window.broadcastPlayerData?.nonce || '';

                                    console.log('⚠️ Fetching VAST via proxy:', proxyEndpoint);

                                    const headers = {
                                        'Content-Type': 'application/json',
                                    };
                                    if (nonce) {
                                        headers['X-WP-Nonce'] = nonce;
                                    }

                                    const response = await fetch(proxyEndpoint, {
                                        method: 'POST',
                                        headers: headers,
                                        body: JSON.stringify({ url: vastUrl })
                                    });
                                    
                                    if (!response.ok) {
                                        throw new Error(`Proxy fetch failed: ${response.status}`);
                                    }
                                    
                                    const data = await response.json();
                                    if (data.status !== 'success' || !data.content) {
                                        throw new Error('Proxy returned error status or empty content');
                                    }
                                    
                                    const { foundPoster, foundClick, foundAltText } = parseVastXml(data.content);
                                    
                                    if (foundPoster) {
                                        console.log('🖼️ Manually parsed companion poster (Proxy):', foundPoster);
                                        return { posterUrl: foundPoster, clickUrl: foundClick, altText: foundAltText };
                                    } else {
                                        console.warn('⚠️ Could not find StaticResource in proxy VAST.');
                                        return { posterUrl: null, clickUrl: null, altText: null };
                                    }
                                } catch (err) {
                                    console.error('❌ Error fetching/parsing VAST manually:', err);
                                    return { posterUrl: null, clickUrl: null, altText: null };
                                }
                            };
                            
                            // ENABLE MANUAL AUDIO PLAYBACK - IMA SDK doesn't properly handle audio-only ads
                            if (mediaUrl && playerRef.current && !playerRef.current.isDisposed()) {
                                // Mark that we're playing audio ad BEFORE the async fetch
                                // This prevents handlePlaying from resetting state
                                playingAudioAdRef.current = true;
                                isAdPlayingRef.current = true;
                                
                                // Fetch companion poster FIRST, then start audio ad
                                const startAudioAd = async () => {
                                    // Fetch companion poster before showing UI
                                    const { posterUrl, clickUrl, altText } = await fetchCompanionPoster();
                                    
                                    // Check if player is still valid after async operation
                                    if (!playerRef.current || playerRef.current.isDisposed()) {
                                        console.warn('⚠️ Player disposed before audio ad could start');
                                        playingAudioAdRef.current = false;
                                        isAdPlayingRef.current = false;
                                        return;
                                    }
                                    
                                    // Set companion URLs before starting playback
                                    if (posterUrl) {
                                        setAdCompanionPosterUrl(posterUrl);
                                        if (clickUrl) {
                                            console.log('🔗 Manually parsed companion click:', clickUrl);
                                            setAdCompanionClickUrl(clickUrl);
                                        }
                                        if (altText) {
                                            console.log('📝 Manually parsed companion alt text:', altText);
                                            setAdCompanionAltText(altText);
                                        }
                                    }
                                    
                                    // Now proceed with audio ad playback
                                    // Set state BEFORE stopping adsManager
                                    setIsAdRequested(false);
                                    isAdRequestedRef.current = false;
                                    setIsAdPlaying(true);
                                    setIsAdMuted(false);
                                    
                                    // Capture current isPostroll value for closure
                                    const wasPostroll = isPostrollRef.current;
                                    
                                    // Play the audio through Video.js
                                    const player = playerRef.current;
                                    
                                    // Remove any stale loadedmetadata handlers that might reset our flags
                                    player.off('loadedmetadata');
                                    
                                    player.controls(false);
                                
                                // Capture the current playlist item for resuming after preroll
                                const currentItemForResume = upcomingItemRef.current;
                                
                                // Set up ended event to resume content after ad
                                const adEndedHandler = () => {
                                    console.log('🎵 Audio ad ended, wasPostroll:', wasPostroll);
                                    // Analytics: fire complete for audio ads (IMA's COMPLETE does not fire after we take over playback).
                                    analytics.trackAdComplete(currentAudioAdRef.current);
                                    currentAudioAdRef.current = null;
                                    player.off('ended', adEndedHandler);
                                    
                                    // Restore player controls
                                    player.controls(true);
                                    
                                    // Defer UI updates to avoid race conditions - hide Ad UI immediately
                                    setTimeout(() => {
                                        setIsAdPlaying(false);
                                        setIsAdRequested(false);
                                        setIsAudioAd(false);
                                        setAdRemainingTime(null);
                                        setAdDuration(null);
                                        setAdCompanionPosterUrl(null);
                                        setAdCompanionClickUrl(null);
                                    }, 0);
                                    
                                    if (wasPostroll) {
                                        // Postroll: advance to next playlist item
                                        setIsPostroll(false);
                                        isPostrollRef.current = false;
                                        
                                        const playlistItems = player.playlist();
                                        const nextIndex = nextItemAfterPostrollRef.current;
                                        
                                        if (nextIndex !== null && nextIndex < playlistItems.length) {
                                            const nextItem = playlistItems[nextIndex];
                                            console.log('🎵 Postroll ended, loading next item:', nextItem.name);
                                            player.src(nextItem.sources);
                                            if (nextItem.poster) {
                                                player.poster(nextItem.poster);
                                            }
                                            player.playlist.currentItem(nextIndex);
                                            player.playlist.autoadvance(isSeries ? 1 : 0);
                                            nextItemAfterPostrollRef.current = null;
                                            
                                            // Immediately reset audio ad flags since we're loading content now
                                            playingAudioAdRef.current = false;
                                            isAdPlayingRef.current = false;
                                            isAdRequestedRef.current = false;
                                            
                                            player.one('loadedmetadata', () => {
                                                // Only reset if we're not currently playing a new audio ad
                                                if (!playingAudioAdRef.current) {
                                                    console.log('🎵 Content loadedmetadata, playing content');
                                                    player.play();
                                                } else {
                                                    console.log('🎵 Content loadedmetadata, but new audio ad is playing, skipping play');
                                                }
                                            });
                                        } else {
                                            console.log('🎵 Postroll ended, no more items in playlist. Cycling back to FIRST item.');
                                            nextItemAfterPostrollRef.current = null;
                                            
                                            const firstIndex = 0;
                                            const playlistItems = player.playlist();
                                            
                                            if (playlistItems && playlistItems.length > 0) {
                                                const firstItem = playlistItems[firstIndex];
                                                blockPostrollRef.current = true; 
                                                
                                                player.src(firstItem.sources);
                                                if (firstItem.poster) player.poster(firstItem.poster);
                                                player.playlist.currentItem(firstIndex);
                                                player.playlist.autoadvance(isSeries ? 1 : 0);
                                                
                                                // Immediately reset audio ad flags since we're loading content now
                                                playingAudioAdRef.current = false;
                                                isAdPlayingRef.current = false;
                                                isAdRequestedRef.current = false;
                                                
                                                player.one('loadedmetadata', () => {
                                                    // Only process if we're not currently playing a new audio ad
                                                    if (!playingAudioAdRef.current) {
                                                        if (isSeries) {
                                                            // Auto-play for series
                                                            console.log('🔄 First item loaded, auto-playing for series');
                                                            player.play();
                                                        } else {
                                                            // For featured audio/broadcast: reload at beginning, don't autostart
                                                            console.log('🔄 First item loaded, pausing at beginning for featured audio');
                                                            player.currentTime(0);
                                                            player.pause();
                                                        }
                                                    } else {
                                                        console.log('🔄 First item loadedmetadata, but new audio ad is playing, skipping');
                                                    }
                                                });
                                            }
                                        }
                                    } else {
                                        // Preroll: load and play the current playlist item content
                                        console.log('🎵 Preroll ended, resuming content:', currentItemForResume?.name);
                                        if (currentItemForResume && currentItemForResume.sources) {
                                            player.src(currentItemForResume.sources);
                                            if (currentItemForResume.poster) {
                                                player.poster(currentItemForResume.poster);
                                            }
                                            
                                            // Immediately reset audio ad flags since we're loading content now
                                            playingAudioAdRef.current = false;
                                            isAdPlayingRef.current = false;
                                            isAdRequestedRef.current = false;
                                            
                                            player.one('loadedmetadata', () => {
                                                // Only process if we're not currently playing a new audio ad
                                                if (!playingAudioAdRef.current) {
                                                    console.log('🎵 Content loaded, playing');
                                                    player.play();
                                                } else {
                                                    console.log('🎵 Content loadedmetadata, but new audio ad is playing, skipping');
                                                }
                                            });
                                        }
                                    }
                                };
                                
                                // Track when metadata is loaded for time updates
                                let metadataLoaded = false;
                                
                                // Store the known ad duration for accurate remaining time calculation
                                const knownAdDuration = adData.duration;
                                
                                // Set initial remaining time to full duration
                                setAdRemainingTime(knownAdDuration);
                                setAdDuration(knownAdDuration);
                                
                                // Set up time update for progress tracking - only after metadata loads
                                const adTimeUpdateHandler = () => {
                                    if (!metadataLoaded) return;
                                    const currentTime = player.currentTime();
                                    const remaining = knownAdDuration - currentTime;
                                    if (remaining >= 0) {
                                        setAdRemainingTime(remaining);
                                    }
                                    // Update skip countdown for manual audio ads
                                    if (skipTimeOffset > 0) {
                                        const skipRemaining = Math.max(skipTimeOffset - currentTime, 0);
                                        setAdSkipTimeOffset(skipRemaining);
                                    }
                                };
                                
                                player.on('timeupdate', adTimeUpdateHandler);
                                
                                // Clean up time update on ended
                                player.one('ended', () => {
                                    player.off('timeupdate', adTimeUpdateHandler);
                                });
                                
                                // Wait for loadedmetadata before playing
                                player.one('loadedmetadata', () => {
                                    metadataLoaded = true;
                                    console.log('🎵 Audio ad metadata loaded, duration:', player.duration());
                                    // Analytics: fire impression for audio ads here (IMA STARTED path skips audio).
                                    analytics.trackAdImpression(currentAudioAdRef.current);

                                    const playPromise = player.play();
                                    if (playPromise !== undefined) {
                                        playPromise.then(() => {
                                            console.log('✅ Audio ad playback started');
                                        }).catch(error => {
                                            console.warn('⚠️ Audio ad playback prevented:', error);
                                        });
                                    }
                                });
                                
                                // NOW load the audio ad source into the player
                                    console.log('🎵 Loading audio ad source:', mediaUrl);
                                    player.src({ type: 'audio/mpeg', src: mediaUrl });
                                    player.muted(false);
                                    
                                    // Register ended handler for audio ad completion
                                    player.one('ended', adEndedHandler);
                                };
                                
                                // Start the async audio ad process
                                startAudioAd();
                                
                                return; // Exit early - we're handling audio playback ourselves
                            }
                        } catch (e) {
                            console.warn('⚠️ Error extracting audio URL, falling back to IMA SDK:', e);
                        }
                    } else {
                        console.log('🎥 Video ad detected - setting isAudioAd to FALSE');
                        setIsAudioAd(false);
                    }

                    setIsAdRequested(false);
                    isAdRequestedRef.current = false;
                    setIsAdPlaying(true);
                    isAdPlayingRef.current = true;

                    // Auto-start ad for all devices with audio enabled
                    try {
                        // Ensure ad volume is set to max
                        if (typeof adsManager.setVolume === 'function') {
                            adsManager.setVolume(1);
                            console.log('🔊 Ad volume set to 1 (max)');
                        }
                        // Ensure player is unmuted
                        if (playerRef.current && !playerRef.current.isDisposed()) {
                            playerRef.current.muted(false);
                            console.log('🔊 Player unmuted');
                        }
                        setIsAdMuted(false);
                        
                        if (adDisplayContainerRef.current && !adContainerInitializedRef.current) {
                            try {
                                adDisplayContainerRef.current.initialize();
                                adContainerInitializedRef.current = true;
                            } catch (initErr) {
                                console.warn('⚠️ AdDisplayContainer initialize failed before start:', initErr);
                            }
                        }
                        adsManager.start();
                    } catch (e) {
                        console.warn('⚠️ Auto-start ad failed:', e);
                    }
                });

                adsManager.addEventListener(window.google.ima.AdEvent.Type.PAUSED, () => {
                    console.log('⏸️ Ad PAUSED event fired (user clicked ad) — auto-resuming to match audio ad behavior');
                    // Match the audio ad: clicking the creative opens the click-through in a
                    // new tab but must NOT pause playback. IMA pauses the linear ad on click,
                    // so immediately resume it (and never surface the resume overlay).
                    if (adsManagerRef.current && typeof adsManagerRef.current.resume === 'function') {
                        adsManagerRef.current.resume();
                    }
                });

                adsManager.addEventListener(window.google.ima.AdEvent.Type.RESUMED, () => {
                    console.log('▶️ Ad RESUMED event fired');
                    setIsAdPaused(false);
                });

                adsManager.addEventListener(window.google.ima.AdEvent.Type.STARTED, (adEvent) => {
                    console.log('▶️ Ad STARTED event fired');
                    // Skip impression here for audio ads — they fire their own impression from loadedmetadata in the manual branch below.
                    if (!playingAudioAdRef.current) {
                        analytics.trackAdImpression(adEvent.getAd());
                    }
                    setIsAdPaused(false);
                    if (playerRef.current && !playerRef.current.isDisposed()) {
                        playerRef.current.controls(false);
                    }
                    if (adsManagerRef.current && typeof adsManagerRef.current.setVolume === 'function') {
                        adsManagerRef.current.setVolume(1);
                    }
                    if (playerRef.current && !playerRef.current.isDisposed()) {
                        playerRef.current.muted(false);
                    }
                    setIsAdMuted(false);

                    // Fallback timer for ad progress
                    if (adTimerIntervalRef.current) {
                        clearInterval(adTimerIntervalRef.current);
                    }
                    const ad = adEvent.getAd();
                    const duration = ad.getDuration();
                    // Overlay owns skip: drive our skip countdown off the SAME fallback timer
                    // that drives the "Anuncio" countdown, so the two can never desync.
                    const skipGate = Math.min(CUSTOM_AD_SKIP_OFFSET_SECONDS, duration);
                    console.log('📦 Ad STARTED custom skip gate:', skipGate);
                    setAdSkipTimeOffset(skipGate);
                    setAdDuration(duration);
                    setAdRemainingTime(duration);
                    let adElapsed = 0;
                    adTimerIntervalRef.current = setInterval(() => {
                        adElapsed += 1;
                        setAdSkipTimeOffset(Math.max(skipGate - adElapsed, 0));
                        setAdRemainingTime(prev => {
                            const next = (prev || 0) - 1;
                            if (next < 0) {
                                clearInterval(adTimerIntervalRef.current);
                                console.log('Ad timer finished. Forcing cleanup.');
                                
                                // Force cleanup if CONTENT_RESUME_REQUESTED doesn't fire
                                setIsAdPlaying(false);
                                isAdPlayingRef.current = false;
                                setIsAdRequested(false);
                                isAdRequestedRef.current = false;
                                setIsAudioAd(false);
                                setAdRemainingTime(null);
                                setAdDuration(null);
                                setIsAdMuted(false);
                                setAdCompanionPosterUrl(null);
                                setAdCompanionClickUrl(null);

                                if (playerRef.current && !playerRef.current.isDisposed()) {
                                    playerRef.current.muted(false);
                                    playerRef.current.controls(true);
                                    if (isPostroll) {
                                        setIsPostroll(false);
                                        isPostrollRef.current = false;
                                        playerRef.current.playlist.autoadvance(isSeries ? 1 : 0);
                                        playerRef.current.playlist.next();
                                    } else if (playerRef.current.paused()) {
                                        playerRef.current.play();
                                    }
                                }
                                return 0;
                            }
                            return next;
                        });
                    }, 1000);

                    try {
                        const adObj = adEvent.getAd();
                        let companionUrl = null;
                        let companionClick = null;
                        if (adObj && typeof adObj.getCompanionAds === 'function') {
                            let s = new google.ima.CompanionAdSelectionSettings();    
                            s.resourceType = google.ima.CompanionAdSelectionSettings.ResourceType.STATIC;
                            s.creativeType = google.ima.CompanionAdSelectionSettings.CreativeType.IMAGE;
                            s.sizeCriteria = google.ima.CompanionAdSelectionSettings.SizeCriteria.IGNORE;
                            
                            const companions = adObj.getCompanionAds(1920, 1080, s);
                            console.log('📦 Companion ads (STARTED):', companions);
                            
                            if (Array.isArray(companions)) {
                                for (const c of companions) {
                                    let urlCandidate = null;
                                    if (typeof c.getResourceValue === 'function') urlCandidate = c.getResourceValue();
                                    else if (typeof c.getContent === 'function') urlCandidate = c.getContent();
                                    else if (c.resourceUrl) urlCandidate = c.resourceUrl;
                                    else if (c.staticUrl) urlCandidate = c.staticUrl;
                                    else if (c.url) urlCandidate = c.url;
                                    console.log('urlCandidate:', urlCandidate);
                                    
                                    if (urlCandidate) {
                                        const urlStr = String(urlCandidate);
                                        if (urlStr.startsWith('http') || urlStr.startsWith('//')) {
                                            companionUrl = urlStr;
                                        } else if (urlStr.trim().startsWith('<')) {
                                            // Handle HTML content (e.g. HTMLResource)
                                            const srcMatch = urlStr.match(/src=["'](.*?)["']/);
                                            if (srcMatch && srcMatch[1]) {
                                                companionUrl = srcMatch[1];
                                            }
                                            const hrefMatch = urlStr.match(/href=["'](.*?)["']/);
                                            if (hrefMatch && hrefMatch[1]) {
                                                companionClick = hrefMatch[1];
                                            }
                                        }

                                        if (companionUrl) {
                                            console.log('String companionUrl', companionUrl);
                                            
                                            let clickCandidate = null;
                                            if (typeof c.getClickThroughUrl === 'function') clickCandidate = c.getClickThroughUrl();
                                            else if (typeof c.getClickThroughURL === 'function') clickCandidate = c.getClickThroughURL();
                                            else if (typeof c.getCompanionClickThroughUrl === 'function') clickCandidate = c.getCompanionClickThroughUrl();
                                            else if (c.clickThroughUrl) clickCandidate = c.clickThroughUrl;
                                            else if (c.clickThroughURL) clickCandidate = c.clickThroughURL;
                                            else if (c.companionClickThroughUrl) clickCandidate = c.companionClickThroughUrl;
                                            
                                            if (clickCandidate && (String(clickCandidate).startsWith('https') || String(clickCandidate).startsWith('//'))) {
                                                companionClick = String(clickCandidate);
                                            }
                                            break;
                                        }
                                    }
                                }
                            }
                        }
                        console.log('Companion Click', companionClick);
                        
                        if (companionUrl) {
                            console.log('🖼️ Companion StaticResource detected:', companionUrl);
                            setAdCompanionPosterUrl(companionUrl);
                            if (companionClick) {
                                setAdCompanionClickUrl(companionClick);
                            } else {
                                setAdCompanionClickUrl(null);
                            }
                        } else {
                            console.log('ℹ️ No companion StaticResource found on STARTED');
                        }
                    } catch (companionErr) {
                        console.warn('⚠️ Error reading companion ad data:', companionErr);
                    }
                });

                adsManager.addEventListener(window.google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, () => {
                    console.log('⏸️ CONTENT_PAUSE_REQUESTED (handled by IMA)');
                    if (playerRef.current && !playerRef.current.isDisposed()) {
                        playerRef.current.pause();
                        playerRef.current.muted(false);
                        playerRef.current.controls(false);
                    }
                    if (adsManagerRef.current && typeof adsManagerRef.current.setVolume === 'function') {
                        adsManagerRef.current.setVolume(1);
                    }
                    setIsAdMuted(false);
                    
                    // Resume the ad playback after content is paused
                    if (adsManagerRef.current && typeof adsManagerRef.current.resume === 'function') {
                        try {
                            console.log('▶️ Resuming ad playback');
                            adsManagerRef.current.resume();
                        } catch (e) {
                            console.warn('⚠️ Error resuming ad:', e);
                        }
                    }
                });
                adsManager.addEventListener(window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () => {
                    console.log('▶️ CONTENT_RESUME_REQUESTED - resuming content');
                    
                    // Mark the time content started/resumed. 
                    // This is used to block VMAP postrolls that fire immediately after prerolls.
                    contentStartTimeRef.current = Date.now();
                    console.log('⏱️ Content start time set:', contentStartTimeRef.current);

                    console.log('🔄 BEFORE reset: isAudioAd will be set to false, isAdPlaying will be set to false');
                    if (adTimerIntervalRef.current) {
                        clearInterval(adTimerIntervalRef.current);
                        adTimerIntervalRef.current = null;
                    }
                    
                    // Keep postroll blocked - it's already true from initialization
                    // We'll unblock it after content has been playing for a bit
                    console.log('🛡️ Keeping postroll blocked during content transition');
                    blockPostrollRef.current = true;
                    
                    // After 1 second of content playback, allow postroll to fire when content actually ends
                    setTimeout(() => {
                        blockPostrollRef.current = false;
                        contentHasPlayedRef.current = true;
                        console.log('🛡️ Postroll block released - content has been playing, postroll now allowed on content end');
                    }, 1000);

                    // Reset UI immediately
                    console.log('🔄 Setting isAudioAd=false, isAdPlaying=false NOW');
                    setIsAudioAd(false); 
                    setIsAdPlaying(false);
                    setAdRemainingTime(null);
                    setAdDuration(null);
                    setIsAdMuted(null); 
                    setAdCompanionPosterUrl(null);
                    setAdCompanionClickUrl(null);

                    // Reset logic refs immediately so handlePlay allows playback
                    isAdPlayingRef.current = false;
                    isAdRequestedRef.current = false;

                    if (player && !player.isDisposed()) {
                        player.muted(false);
                        player.controls(true);
                    }

                    // Use REF to check postroll state to avoid stale closure issues
                    if (isPostrollRef.current) {
                        setIsPostroll(false);
                        isPostrollRef.current = false;
                        player.playlist.autoadvance(isSeries ? 1 : 0);
                        
                        // Add small delay to prevent race conditions with ad manager cleanup
                        setTimeout(() => {
                            console.log('🔄 Postroll finished, advancing to next item (delayed)');
                            player.playlist.next();
                        }, 100);
                    } else {
                        const upcomingItem = upcomingItemRef.current;
                        if (upcomingItem && upcomingItem.poster) {
                            player.poster(upcomingItem.poster);
                        }

                        // Safely play the content
                        const playContent = () => {
                            if (player.paused()) {
                                const playPromise = player.play();
                                if (playPromise !== undefined) {
                                    playPromise.then(() => {
                                        console.log('▶️ Content playback started successfully');
                                    }).catch(error => {
                                        console.warn('⚠️ Content playback prevented:', error);
                                    });
                                }
                            }
                        };

                        if (isSeries) {
                            // Force play for series
                            console.log('🔄 Auto-playing content for series after ad');
                            player.currentTime(0); // Ensure we start from the beginning
                            const playPromise = player.play();
                            if (playPromise !== undefined) {
                                playPromise.then(() => {
                                    console.log('▶️ Content playback started successfully (series)');
                                    contentHasPlayedRef.current = true;
                                }).catch(e => console.warn(e));
                            }
                        } else {
                            playContent();
                            contentHasPlayedRef.current = true; // Optimistically set for non-series
                        }
                    }

                    player.trigger('ads-ad-ended');
                });

                // Update remaining time during ad playback
                adsManager.addEventListener(window.google.ima.AdEvent.Type.AD_PROGRESS, (event) => {
                    // If we've already decided the ad is over (UI is hidden), stop any background processing
                    if (!isAdPlayingRef.current && !playingAudioAdRef.current) {
                        console.log('🛑 AD_PROGRESS ignored & AdsManager stopped: Ad UI is closed.');
                        try {
                            adsManager.stop();
                        } catch (e) {}
                        return;
                    }

                    const ad = event.getAd();
                    const data = event.getAdData();
                    
                    // Debug volume state occasionally (every ~5 seconds approx or just verify on progress)
                    if (data && data.currentTime && Math.floor(data.currentTime) % 5 === 0) {
                         console.log(`🔊 Ad Progress: Time=${data.currentTime}, Player Volume=${playerRef.current?.volume()}, Player Muted=${playerRef.current?.muted()}`);
                    }

                    console.log('IMA: AD_PROGRESS event data:', data); // Added debug
                    if (ad) {
                        const duration = ad.getDuration();
                        const currentTime = data && typeof data.currentTime === 'number' ? data.currentTime : null;
                        // console.log('⏱️ Ad duration:', duration, 'currentTime:', currentTime);
                        if (!isNaN(duration) && duration > 0 && currentTime !== null) {
                            const remaining = Math.max(duration - currentTime, 0);
                            // console.log('⏱️ Updating adRemainingTime to:', remaining);
                            setAdRemainingTime(remaining);

                            // Update skip countdown
                            const skipOffset = typeof ad.getSkipTimeOffset === 'function' ? ad.getSkipTimeOffset() : -1;
                            if (skipOffset > 0) {
                                const skipRemaining = Math.max(skipOffset - currentTime, 0);
                                setAdSkipTimeOffset(skipRemaining);
                            } else if (skipOffset === 0) {
                                setAdSkipTimeOffset(0);
                            }
                        } else {
                            console.log('⚠️ Unable to compute remaining time from AD_PROGRESS');
                        }
                    } else {
                        console.log('⚠️ AD_PROGRESS without ad instance');
                    }
                });
                
                adsManager.addEventListener(window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED, cleanupAdsManager);

                // Analytics: IMA click/complete/skip. Audio ads route through the manual branch in LOADED,
                // which calls the same track* helpers directly — no double-fire because IMA's click/complete/skip
                // don't fire once we've taken over playback via the host Video.js player.
                adsManager.addEventListener(window.google.ima.AdEvent.Type.CLICK,    (e) => analytics.trackAdClick(e.getAd()));
                adsManager.addEventListener(window.google.ima.AdEvent.Type.COMPLETE, (e) => analytics.trackAdComplete(e.getAd()));
                adsManager.addEventListener(window.google.ima.AdEvent.Type.SKIPPED,  (e) => analytics.trackAdSkip(e.getAd(), playerRef.current?.currentTime() ?? 0));

                try {
                    const { clientWidth, clientHeight } = videoNode.parentElement;
                    console.log(`📐 Initializing adsManager with dimensions: ${clientWidth}x${clientHeight}`);
                    adsManager.init(clientWidth, clientHeight, window.google.ima.ViewMode.NORMAL);
                    
                    // Set volume immediately after initialization
                    if (typeof adsManager.setVolume === 'function') {
                        adsManager.setVolume(1);
                        console.log('🔊 Ad volume set to 1 immediately after init');
                    }
                } catch (adError) {
                    onAdError({ getError: () => adError });
                }
            };

            adsLoader.addEventListener(window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, onAdsManagerLoaded, false);
            adsLoader.addEventListener(window.google.ima.AdErrorEvent.Type.AD_ERROR, onAdError, false);
        }

        const requestAds = (adTagUrl) => {
            console.log('📡 requestAds called with URL:', adTagUrl);
            console.log('IMA: Requesting Ad Tag:', adTagUrl); // Added debug
            
            // Track if this is a VMAP request (contains multiple ad breaks) vs direct VAST (single ad)
            // VMAP URLs typically contain "vmap" in the filename
            isVmapRequestRef.current = adTagUrl.toLowerCase().includes('vmap');
            console.log('📦 Is VMAP request:', isVmapRequestRef.current);
            
            // PREVENT RACE: Pause content immediately so it can't start before ad LOADED event sets isAdPlaying
            if (playerRef.current && !playerRef.current.isDisposed()) {
                const contentPlayer = playerRef.current;
                if (!contentPlayer.paused()) {
                    console.log('⛔ Forcing immediate content pause before ad request (race prevention)');
                    contentPlayer.pause();
                }
                // Disable autoplay temporarily while ad is requested/playing
                contentPlayer.autoplay(false);
                // Hide controls while ad loading start sequence; will be restored on ads-ad-ended
                contentPlayer.controls(false);
            }

            currentAdTagUrlRef.current = adTagUrl; // Store for fallback fetch
            if (!adsLoaderRef.current) {
                console.log('❌ No adsLoader available');
                return;
            }
            cleanupAdsManager();

            // Mark ad as requested (not yet playing) so any auto play attempts are blocked until LOADED
            setIsAdRequested(true);
            isAdRequestedRef.current = true;
            setIsAdPlaying(false);
            isAdPlayingRef.current = false;

            // Ensure the ad container is initialized in a user-gesture context when possible
            if (adDisplayContainerRef.current && !adContainerInitializedRef.current) {
                try {
                    console.log('🔧 Initializing AdDisplayContainer before requesting ads');
                    adDisplayContainerRef.current.initialize();
                    adContainerInitializedRef.current = true;
                } catch (initErr) {
                    console.warn('⚠️ AdDisplayContainer initialize failed before requestAds:', initErr);
                }
            }

            // Sanitize VAST/VMAP XML: escape bare & that are not already part of an XML entity.
            // This prevents IMA SDK parse errors (error 100) from malformed XML sources.
            const sanitizeVastXml = (xml) => {
                // Replace & that is NOT followed by amp; lt; gt; quot; apos; or # (numeric entity)
                return xml.replace(/&(?!amp;|lt;|gt;|quot;|apos;|#)/g, '&amp;');
            };

            // Helper to submit the ad request to IMA SDK
            const submitRequest = (vastXml) => {
                // Guard: adsLoader may have been cleaned up while we were fetching
                if (!adsLoaderRef.current) {
                    console.log('❌ adsLoader gone before submitRequest');
                    return;
                }
                const adsRequest = new window.google.ima.AdsRequest();
                if (vastXml) {
                    // Sanitize the XML before passing to IMA SDK
                    const sanitized = sanitizeVastXml(vastXml.trim());
                    console.log('📄 VAST XML first 300 chars:', sanitized.substring(0, 300));
                    // Pass pre-fetched XML directly — bypasses IMA SDK's cross-origin fetch
                    adsRequest.adsResponse = sanitized;
                    console.log('✅ Using pre-fetched VAST XML via adsResponse');
                } else {
                    // Fallback: let IMA SDK fetch it (works when server has proper CORS headers)
                    adsRequest.adTagUrl = adTagUrl;
                    console.log('📎 Falling back to adTagUrl for IMA SDK to fetch');
                }
                adsRequest.linearAdSlotWidth = player.el().clientWidth;
                adsRequest.linearAdSlotHeight = player.el().clientHeight;

                // Set mobile-friendly ad request parameters
                adsRequest.setAdWillPlayMuted(false);
                adsRequest.setAdWillAutoPlay(false);

                console.log('🚀 Requesting ads from IMA SDK...');
                adsLoaderRef.current.requestAds(adsRequest);
            };

            // Pre-fetch the VAST/VMAP XML ourselves to avoid CORS issues.
            // The IMA SDK fetches from its iframe (imasdk.googleapis.com) which may be
            // blocked by CORS. By fetching from our page origin (same-origin in production,
            // or local fallback in development) we bypass this entirely.
            fetch(adTagUrl)
                .then(response => {
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    return response.text();
                })
                .then(text => {
                    if (text && (text.includes('<VAST') || text.includes('<vmap') || text.includes('<VMAP'))) {
                        submitRequest(text);
                    } else {
                        throw new Error('Response does not look like VAST/VMAP XML');
                    }
                })
                .catch(fetchErr => {
                    console.warn('⚠️ Direct VAST fetch failed:', fetchErr.message);
                    // Development fallback: try loading from local /media/xml/ with .xml extension
                    try {
                        const urlObj = new URL(adTagUrl);
                        const localPath = urlObj.pathname.replace(/\.php(\?.*)?$/, '.xml');
                        console.log('🔄 Trying local XML fallback:', localPath);
                        fetch(localPath)
                            .then(resp => {
                                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                                return resp.text();
                            })
                            .then(localText => {
                                if (localText && (localText.includes('<VAST') || localText.includes('<vmap') || localText.includes('<VMAP'))) {
                                    console.log('✅ Local VAST XML fallback succeeded');
                                    submitRequest(localText);
                                } else {
                                    throw new Error('Local file is not VAST/VMAP XML');
                                }
                            })
                            .catch(localErr => {
                                console.warn('⚠️ Local VAST fallback also failed:', localErr.message, '— letting IMA SDK try adTagUrl directly');
                                submitRequest(null);
                            });
                    } catch (urlParseErr) {
                        console.warn('⚠️ Could not parse ad URL for local fallback — letting IMA SDK try adTagUrl directly');
                        submitRequest(null);
                    }
                });
        };

        player.on('playlistitem', (evt, item) => {
            console.log('💿 playlistitem event fired for:', item.name);

            // Analytics: fire next/prev for real transitions (not the initial load). We keep our
            // own prev-item ref because `beforeplaylistitem` already updated `upcomingItemRef` to
            // the new item before this handler runs.
            const prevItem = previousPlaylistItemRef.current;
            const prevIndex = previousPlaylistIndexRef.current;
            const newIndex = player.playlist.currentItem();
            if (prevItem && prevIndex !== null && newIndex !== prevIndex) {
                if (newIndex > prevIndex) analytics.trackNext(prevItem, item);
                else                      analytics.trackPrev(prevItem, item);
            }
            previousPlaylistIndexRef.current = newIndex;
            previousPlaylistItemRef.current = item;
            // New episode: next play should be a trackPlay (not trackResume) and milestones reset.
            firstPlayFiredRef.current = false;
            milestonesFiredRef.current = new Set();

            upcomingItemRef.current = item;
            contentHasPlayedRef.current = false; // Reset for new item
            contentStartTimeRef.current = 0; // Reset content start time
            if (item.poster) {
                player.poster(item.poster);
            }
            if (onPlaylistItemChange) {
                onPlaylistItemChange(item);
            }

            if (isSeries) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                // For broadcast player, scroll the playlist to show the current item
                setTimeout(() => {
                    const currentIndex = player.playlist.currentItem();
                    const playlistContainer = playlistContainerRef.current;
                    if (playlistContainer) {
                        const items = playlistContainer.querySelectorAll('.vjs-playlist-item');
                        const currentItem = items[currentIndex];
                        if (currentItem) {
                            currentItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
                        }
                    }
                }, 100);
            }

                        if (!isSeries && isTextExpanded) {

                            handleTextExpansion(false);

                        }

                        

                        // --- MODIFIED LOGIC FOR IMMEDIATE PLAYBACK ---

                        // Only attempt to play if it's not the initial load of the very first item

                        if (!isInitialPlaylistItemSetRef.current) { 

                            if (!item.prerollAdUrl || !prerollAdEnabled) {

                                const playPromise = player.play();

                                if (playPromise !== undefined) {

                                    playPromise.then(() => {

                                        console.log('▶️ Content played directly on playlist item change (no preroll)');

                                    }).catch(error => {

                                        console.warn('Playback prevented on playlist item change:', error);

                                    });

                                }

                            } else {

                                // If there's a preroll, ensure content is paused until ad playback is resolved.

                                // requestAds will handle starting the ad and then the content.

                                if (!player.paused()) {

                                    console.log('⛔ Pausing content for preroll ad on playlist item change');

                                    player.pause();

                                }

                            }

                        } else {

                            // For the initial load of a series player (autoplay false), ensure it's paused.

                            // This preserves the default behavior of not auto-playing on first load.

                            if (isSeries && !player.paused()) {

                                player.pause();

                            }

                        }

                        // Reset the flag after the first playlistitem event has been processed

                        isInitialPlaylistItemSetRef.current = false;

                        // --- END MODIFIED LOGIC ---

                    });

        player.on('beforeplaylistitem', (evt, item) => {
            console.log('⏭️ beforeplaylistitem event fired for:', item.name);
            console.log('🔍 Checking for prerollAdUrl:', item.prerollAdUrl);
            console.log('👆 manualPlaylistClickRef.current:', manualPlaylistClickRef.current);
            upcomingItemRef.current = item;
            
            // Skip preroll if user manually clicked on a playlist item
            if (manualPlaylistClickRef.current) {
                console.log('👆 User manually clicked playlist item, skipping preroll');
                manualPlaylistClickRef.current = false; // Reset the flag
                
                // Reset all ad state when user manually clicks
                if (playingAudioAdRef.current) {
                    console.log('👆 Stopping current audio ad');
                    player.pause();
                }
                playingAudioAdRef.current = false;
                setIsAdPlaying(false);
                isAdPlayingRef.current = false;
                setIsAdRequested(false);
                isAdRequestedRef.current = false;
                setIsAudioAd(false);
                setAdRemainingTime(null);
                setAdDuration(null);
                setAdSkipTimeOffset(null);
                setAdCompanionPosterUrl(null);
                setAdCompanionClickUrl(null);
                setIsPostroll(false);
                isPostrollRef.current = false;
                
                if (item.poster) {
                    player.poster(item.poster);
                }
                return; // Skip preroll ad request
            }
            
            if (item.prerollAdUrl && prerollAdEnabled) {
                console.log('✅ prerollAdUrl found, requesting ads');
                // Attempt to initialize the ad container in response to this user gesture
                if (adDisplayContainerRef.current && !adContainerInitializedRef.current) {
                    try {
                        console.log('🔧 Initializing AdDisplayContainer from beforeplaylistitem gesture');
                        adDisplayContainerRef.current.initialize();
                        adContainerInitializedRef.current = true;
                    } catch (initErr) {
                        console.warn('⚠️ AdDisplayContainer initialize failed in beforeplaylistitem:', initErr);
                    }
                }
                requestAds(item.prerollAdUrl);
            } else if (item.poster) {
                console.log('📸 No ad URL, setting poster:', item.poster);
                player.poster(item.poster);
            }
        });

        if (playlist.length > 0) {
            // For broadcast, we start at last item; for series, at first item
            const startIndex = !isSeries ? playlist.length - 1 : 0;
            const startItem = playlist[startIndex];
            console.log('📋 Start playlist item:', startItem.name, 'at index:', startIndex);
            console.log('🔍 Start item prerollAdUrl:', startItem.prerollAdUrl);
            
            player.poster(startItem.poster);
            upcomingItemRef.current = startItem;

            player.one('play', (event) => {
                    console.log('▶️ First play event triggered');
                    // Only consider requesting an ad here for the initial playlist item
                    // and only if an ad isn't already requested/playing.
                    let currentIndex = 0;
                    try {
                        if (typeof player.playlist?.currentItem === 'function') {
                            currentIndex = player.playlist.currentItem();
                        }
                    } catch {}

                    if (currentIndex !== startIndex) {
                        console.log('⏭️ Skipping first-play ad request: not start item (expected:', startIndex, 'got:', currentIndex, ')');
                        return;
                    }
                    if (isAdRequestedRef.current || isAdPlaying) {
                        console.log('⏭️ Skipping first-play ad request: ad already requested/playing');
                        return;
                    }

                    if (startItem.prerollAdUrl && prerollAdEnabled) {
                        console.log('🎬 Start item has prerollAdUrl, preventing default and requesting ad');
                        event.preventDefault();

                        // Hard-stop content playback so it cannot “win” the race
                        if (!player.paused()) {
                            console.log('⛔ Forcing content pause before ad request');
                            player.pause();
                        }

                        if (adDisplayContainerRef.current && !adContainerInitializedRef.current) {
                            try {
                                console.log('🔧 Initializing AdDisplayContainer');
                                adDisplayContainerRef.current.initialize();
                                adContainerInitializedRef.current = true;
                            } catch (initErr) {
                                console.warn('⚠️ AdDisplayContainer initialize failed on first play:', initErr);
                            }
                        }

                        requestAds(startItem.prerollAdUrl);
                    } else {
                        console.log('⚠️ Start item has NO prerollAdUrl, allowing content to play.');
                        // Let Video.js continue normal play
                    }
                });
            
            if (onPlaylistItemChange) {
                onPlaylistItemChange(startItem);
            }
        }
    }, [playlist, isSeries, onPlaylistItemChange, isTextExpanded, handleTextExpansion, initialIndex]);

    // Add descriptions to playlist items after they're rendered
    const addDescriptionsToPlaylist = useCallback(() => {
        const items = document.querySelectorAll('.vjs-playlist-item');

        items.forEach((item, index) => {
            const playlistItem = playlist[index];

            // Series data does not contain a description - it's a name instead
            if (playlistItem && (playlistItem.description || playlistItem.name)) {
                const nameEl = item.querySelector('.vjs-playlist-name');

                if (isSeries) {
                    if (playlistItem.messageSpeaker === 'Paws & Tales') {
                        // P&T rows: no "Episode N - Speaker" line. Instead the title is
                        // numbered by its position in the season's playlist (the first row
                        // is "1." even when the global episode number is e.g. 22), with a
                        // per-episode Tools for Parents link to the episode page below it.
                        if (nameEl) {
                            const numberedName = (index + 1) + '. ' + playlistItem.name;
                            nameEl.textContent = numberedName;
                            nameEl.title = numberedName;
                        }
                        if (nameEl && !item.querySelector('.vjs-playlist-teaser') && playlistItem.messagePermalink) {
                            // Episode teaser replaces the former Parent Tools pill: the first
                            // sentence of the episode summary, clamped to two lines, with a
                            // "+ More" link on the line below that opens the same bottom sheet.
                            // The teaser previews exactly what the sheet leads with (the full
                            // synopsis), so the link delivers what it promises.
                            const plain = stripHtml(playlistItem.messageSummary || '');
                            const sentenceMatch = plain.match(/^[\s\S]{10,140}?[.!?](?=\s|$)/);
                            let teaser = sentenceMatch ? sentenceMatch[0] : plain.slice(0, 90);
                            if (!sentenceMatch && plain.length > 90) teaser += '…';

                            const teaserEl = document.createElement('div');
                            teaserEl.className = 'vjs-playlist-teaser';
                            teaserEl.style.cssText = 'margin-top: 6px; font-family: "Burbank Regular", sans-serif; font-weight: 200; font-size: 15px; line-height: 21px; color: rgba(255,255,255,0.85); white-space: normal;';

                            if (teaser) {
                                // Two-line clamp mirrors the season synopsis treatment (clamped
                                // text, link on the line below).
                                const textEl = document.createElement('div');
                                textEl.style.cssText = 'display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;';
                                textEl.textContent = teaser;
                                teaserEl.appendChild(textEl);
                            }

                            const moreEl = document.createElement('a');
                            moreEl.href = playlistItem.messagePermalink;
                            // With no teaser to give it context, a bare "+ More" is meaningless —
                            // fall back to the explicit feature name.
                            moreEl.textContent = teaser ? '+ ' + t.more : t.parentTools;
                            moreEl.title = t.toolsForParents;
                            moreEl.setAttribute('aria-label', t.toolsForParents);
                            // Negative margins cancel the padding visually while keeping a ~44px touch target
                            moreEl.style.cssText = 'display: inline-block; color: #E0660D; font-weight: 400; text-decoration: none; white-space: nowrap; padding: 12px 10px; margin: -10px -10px -12px;';
                            // The row's own click/tap switches episodes — keep link taps from doing
                            // that too. Video.js synthesizes 'tap' from raw touch events on the row
                            // (and its touch handling suppresses the link's synthetic click), so the
                            // touch events must be stopped here as well, not just 'click'.
                            ['touchstart', 'touchend'].forEach((evt) =>
                                moreEl.addEventListener(evt, (e) => e.stopPropagation())
                            );
                            // Opens the ParentToolsModal bottom sheet (SeriesPlayer listens);
                            // the href stays as a long-press/no-JS fallback to the episode page.
                            moreEl.addEventListener('click', (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                window.dispatchEvent(new CustomEvent('pnt:parent-tools-open', {
                                    detail: { guid: playlistItem.guid ?? playlistItem['ifl:guid'] }
                                }));
                            });
                            teaserEl.appendChild(moreEl);
                            nameEl.parentNode.appendChild(teaserEl);
                        }
                    } else if (nameEl && !item.querySelector('.vjs-playlist-description')) {
                        const descEl = document.createElement('div');
                        descEl.className = 'vjs-playlist-description';
                        const label = playlistItem.mediaType === 'video' ? t.video : t.message;
                        descEl.textContent = label + ' ' + playlistItem.messageNumber + ' - ' + playlistItem.messageSpeaker;
                        // descEl.style.cssText = 'font-size: 20px; color: #000; background-color: #fff';
                        descEl.style.cssText = 'color: #000; background-color: #fff';
                        nameEl.parentNode.appendChild(descEl, nameEl);
                    }
                } else {
                    if (nameEl && !item.querySelector('.vjs-playlist-description')) {
                        const descEl = document.createElement('div');
                        descEl.className = 'vjs-playlist-description';
                        descEl.textContent = playlistItem.description;
                        descEl.style.cssText = 'font-size: 12px; color: #fff';
                        nameEl.parentNode.insertBefore(descEl, nameEl);
                    }
                }
            }
        });
    }, [playlist, isSeries]);

    // Normalize playlist DOM so title container sits below the thumbnail (VideoJS places it inside <picture>)
    const normalizePlaylistDOM = useCallback(() => {
        const items = document.querySelectorAll('.vjs-playlist-item');
        items.forEach(item => {
            const picture = item.querySelector('picture.vjs-playlist-thumbnail');
            if (picture) {
                const titleContainer = picture.querySelector('.vjs-playlist-title-container');
                if (titleContainer) {
                    // move title container directly under the li, after the picture
                    item.appendChild(titleContainer);
                }
            }
        });
    }, []);

    // Helper function to get playlist items with caching
    const getPlaylistItems = useCallback(() => {
        if (!playlistContainerRef.current) return [];
        return Array.from(playlistContainerRef.current.querySelectorAll('.vjs-playlist-item'));
    }, []);

    // Move Up Next and Now Playing text into thumbnail containers for proper positioning
    const moveTextIntoThumbnails = useCallback(() => {
        console.log('🔄 moveTextIntoThumbnails called');
        const allItems = getPlaylistItems();
        console.log('📋 Found', allItems.length, 'playlist items');

        allItems.forEach((item, index) => {
            const thumbnail = item.querySelector('.vjs-playlist-thumbnail');
            // console.log(`📦 Item ${index}: thumbnail found:`, !!thumbnail);

            // Up Next label handling
            let upNextText = item.querySelector('.vjs-up-next-text');
            if (!upNextText) {
                upNextText = document.createElement('span');
                upNextText.className = 'vjs-up-next-text';
                // console.log(`✨ Item ${index}: Created new Up Next text`);
            }
            // Always update text and title for translation
            upNextText.textContent = t.upNext;
            upNextText.title = t.upNext;

            const upNextStyle = isSeries
                ? 'position: absolute; bottom: 10px; left: 10px; background: rgba(0,0,0,0.8); color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px; z-index: 10;'
                : 'position: absolute; bottom: 10px; left: 10px; background: rgba(0,0,0,0.8); color: white; padding: 2px 6px; border-radius: 3px; font-size: 11px; z-index: 10;';
            upNextText.style.cssText = upNextStyle;
            if (thumbnail && upNextText.parentNode !== thumbnail) {
                if (!thumbnail.style.position) thumbnail.style.position = 'relative';
                thumbnail.appendChild(upNextText);
                // console.log(`➡️ Item ${index}: Up Next text moved to thumbnail (existed: ${upNextExisted})`);
            } else if (!thumbnail && upNextText.parentNode !== item) {
                item.appendChild(upNextText);
                // console.log(`➡️ Item ${index}: Up Next text added to item (no thumbnail)`);
            } else {
                // console.log(`✅ Item ${index}: Up Next text already in correct position`);
            }

            // Create Now Playing text if it doesn't exist
            let nowPlayingText = item.querySelector('.vjs-playlist-now-playing-text');
            if (!nowPlayingText) {
                nowPlayingText = document.createElement('span');
                nowPlayingText.className = 'vjs-playlist-now-playing-text';
                console.log(`✨ Item ${index}: Created new Now Playing text`);
            }
            // Always update text and title for translation
            nowPlayingText.textContent = t.nowPlaying;
            nowPlayingText.title = t.nowPlaying;
            
            // Set white-space based on language (English stays on one line, Spanish can break)
            const lang = window.broadcastPlayerData?.language || 'en-US';
            const isSpanish = lang.toLowerCase().startsWith('es');
            nowPlayingText.style.whiteSpace = isSpanish ? 'normal' : 'nowrap';

            if (!nowPlayingText.parentNode) {
                if (isSeries) {
                    nowPlayingText.style.cssText = 'position: absolute; top: 10px; left: 10px; background: rgba(255,0,0,0.8); color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px; z-index: 10;';
                    if (thumbnail) {
                        thumbnail.appendChild(nowPlayingText);
                    } else {
                        item.appendChild(nowPlayingText);
                    }
                } else {
                    nowPlayingText.style.cssText = 'position: absolute; top: 5px; left: 5px; background: rgba(255,0,0,0.8); color: white; padding: 2px 6px; border-radius: 3px; font-size: 11px; z-index: 10;';
                    item.appendChild(nowPlayingText);
                }
                console.log(`➡️ Item ${index}: Now Playing text positioned`);
            }

            // Move existing elements into thumbnail if they're not already there
            if (thumbnail) {
                if (upNextText && !thumbnail.contains(upNextText)) {
                    thumbnail.appendChild(upNextText);
                    console.log(`🔁 Item ${index}: Moved existing Up Next text into thumbnail`);
                }

                if (nowPlayingText && !thumbnail.contains(nowPlayingText)) {
                    thumbnail.appendChild(nowPlayingText);
                    console.log(`🔁 Item ${index}: Moved existing Now Playing text into thumbnail`);
                }
            }
        });
        console.log('✅ moveTextIntoThumbnails completed');
    }, [isSeries, getPlaylistItems]);

    // Disable playlist interaction and hide poster when ad is playing
    useEffect(() => {
        if (playlistContainerRef.current) {
            if (isAdPlaying) {
                playlistContainerRef.current.classList.add('vjs-playlist-disabled');
            } else {
                playlistContainerRef.current.classList.remove('vjs-playlist-disabled');
            }
        }
        // Hide/show the video.js poster during ad playback
        const player = playerRef.current;
        if (player && !player.isDisposed()) {
            const posterEl = player.el().querySelector('.vjs-poster');
            if (posterEl) {
                if (isAdPlaying) {
                    posterEl.style.display = 'none';
                    posterEl.style.visibility = 'hidden';
                } else {
                    posterEl.style.display = '';
                    posterEl.style.visibility = '';
                }
            }
        }
    }, [isAdPlaying]);

    useEffect(() => {
        const player = playerRef.current;
        if (player && !player.isDisposed() && playlistContainerRef.current) {
            player.playlistUi(playlistContainerRef.current);

            // Initialize text overlays and descriptions after playlist UI is ready
            setTimeout(() => {
                addDescriptionsToPlaylist();
                normalizePlaylistDOM();
                moveTextIntoThumbnails();
                
                // Add click handlers to playlist items to track manual clicks
                const playlistItems = playlistContainerRef.current.querySelectorAll('.vjs-playlist-item');
                playlistItems.forEach((item) => {
                    // Remove any existing handler first to avoid duplicates
                    item.removeEventListener('click', item._manualClickHandler);
                    item._manualClickHandler = () => {
                        console.log('👆 User clicked playlist item');
                        manualPlaylistClickRef.current = true;
                    };
                    // Use capture phase to set the flag BEFORE the playlist plugin handles the click
                    item.addEventListener('click', item._manualClickHandler, { capture: true });
                });
            }, 100);
        }
    }); // This effect will run on every render to ensure the playlist UI is initialized

    useEffect(() => {
        const player = playerRef.current;

        const updateThumbnailHeight = () => {
            if (playlistContainerRef.current) {
                const selectedItem = playlistContainerRef.current.querySelector('.vjs-selected');
                if (selectedItem) {
                    const img = selectedItem.querySelector('picture.vjs-playlist-thumbnail img');
                    if (img) {
                        const imgHeight = img.offsetHeight;
                        playlistContainerRef.current.style.setProperty('--thumbnail-height', `${imgHeight}px`);
                    }
                }
            }
        };

        const currentPlayer = playerRef.current;
        if (currentPlayer) {
            currentPlayer.on('playlistitem', () => {
                updateThumbnailHeight();
                moveTextIntoThumbnails();
            });
        }
        window.addEventListener('resize', updateThumbnailHeight);
        const timeoutId = setTimeout(() => {
            updateThumbnailHeight();
            moveTextIntoThumbnails();
        }, 500);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', updateThumbnailHeight);
            if (currentPlayer) {
                currentPlayer.off('playlistitem');
            }
            cleanupAdsManager();
            if (player && !player.isDisposed()) {
                player.dispose();
                playerRef.current = null;
            }
        };
    }, [moveTextIntoThumbnails]);

    useEffect(() => {
        const player = playerRef.current;
        if (!player || player.isDisposed()) {
            return;
        }

        const handlePause = () => {
            if (isAdPlaying && adsManagerRef.current) {
                adsManagerRef.current.pause();
            } else if (!isAdPlayingRef.current && !isAdRequestedRef.current) {
                console.log('⏸️ Main content paused');
                setIsContentPaused(true);
                // Browsers fire `pause` right before `ended` at natural end. Suppress so we don't
                // emit a pause immediately followed by a complete for the same moment.
                const dur = player.duration();
                const atEnd = player.ended() || (dur && dur - player.currentTime() < 0.5);
                if (!atEnd) {
                    analytics.trackPause(upcomingItemRef.current, player.currentTime());
                }
            }
        };

        const handlePlay = (event) => {
            // Allow playback if we're playing an audio ad through Video.js
            if (playingAudioAdRef.current) {
                console.log('✅ Allowing audio ad playback through Video.js');
                return;
            }
            // Use refs instead of state to avoid stale closure issues
            if (isAdRequestedRef.current || isAdPlayingRef.current) {
                console.log('⛔ Blocking content play while ad is requested/active');
                event.stopImmediatePropagation();
                if (!player.paused()) {
                    player.pause();
                }
                return;
            }

            // Main content play
            console.log('▶️ Main content playing');
            setIsContentPaused(false);
            if (firstPlayFiredRef.current) {
                analytics.trackResume(upcomingItemRef.current, player.currentTime());
            } else {
                analytics.trackPlay(upcomingItemRef.current);
                firstPlayFiredRef.current = true;
            }
        };

        const handlePlaying = () => {
            // Allow playback if we're playing an audio ad through Video.js
            if (playingAudioAdRef.current) {
                console.log('✅ handlePlaying: playingAudioAdRef is true, allowing audio ad playback');
                return;
            }
            
            // If not an ad, mark content as having played
            if (!isAdPlayingRef.current && !isAdRequestedRef.current) {
                contentHasPlayedRef.current = true;
                
                // Ensure controls are shown during content playback
                player.controls(true);
                
                // UNBLOCK POSTROLL: If content is playing and we didn't just come from an ad (time=0),
                // we must ensure postroll is enabled.
                if (contentStartTimeRef.current === 0 && blockPostrollRef.current) {
                    console.log('🛡️ Unblocking postroll (content playing, no ad resume detected)');
                    blockPostrollRef.current = false;
                }
                
                // SAFETY SYNC: If content is playing (refs are false) but state thinks ad is playing,
                // force the state to reset. This fixes the "UI stuck" issue.
                // BUT: Don't reset if playingAudioAdRef is true (audio ad playing through Video.js)
                if ((isAdPlaying || isAudioAd) && !playingAudioAdRef.current) {
                    console.warn('⚠️ State mismatch detected during content playback. Forcing Ad UI close.');
                    console.log('Diagnostic: isAdPlaying:', isAdPlaying, 'isAudioAd:', isAudioAd, 'Refs:', isAdPlayingRef.current, isAdRequestedRef.current, 'playingAudioAdRef:', playingAudioAdRef.current);
                    setIsAdPlaying(false);
                    setIsAudioAd(false);
                    setAdRemainingTime(null);
                    setAdDuration(null);
                    setIsAdMuted(null);
                    setAdCompanionPosterUrl(null);
                    setAdCompanionClickUrl(null);
                }
            }

            // Use refs instead of state to avoid stale closure issues
            if (isAdRequestedRef.current || isAdPlayingRef.current) {
                if (!player.paused()) {
                    console.log('⛔ Pausing on playing event due to ad requested/active');
                    player.pause();
                }
            }
        };

        // Analytics: seek + progress milestone handlers. Guarded against ad playback so ad-scrub
        // noise (unlikely, but defensive) and the audio-ad timeupdate driver don't leak into content events.
        const handleSeeking = () => {
            if (isAdPlayingRef.current || playingAudioAdRef.current) return;
            // Only capture the "from" position at the start of a seek burst. During a scrub drag,
            // `seeking` fires repeatedly and would otherwise overwrite the real pre-drag position.
            if (seekDebounceRef.current == null) {
                seekFromRef.current = player.currentTime();
            }
        };

        const handleSeeked = () => {
            if (isAdPlayingRef.current || playingAudioAdRef.current) return;
            // Dragging the scrub bar produces many seeked events in quick succession. Debounce so
            // we send one trackSeek per drag (original pre-drag position → final landing point).
            if (seekDebounceRef.current) clearTimeout(seekDebounceRef.current);
            seekDebounceRef.current = setTimeout(() => {
                seekDebounceRef.current = null;
                analytics.trackSeek(upcomingItemRef.current, seekFromRef.current, player.currentTime());
            }, 250);
        };

        const handleTimeUpdate = () => {
            if (isAdPlayingRef.current || playingAudioAdRef.current) return;
            const dur = player.duration();
            if (!dur) return;
            const pct = (player.currentTime() / dur) * 100;
            [25, 50, 75].forEach((m) => {
                if (pct >= m && !milestonesFiredRef.current.has(m)) {
                    milestonesFiredRef.current.add(m);
                    analytics.trackProgress(upcomingItemRef.current, m, player.currentTime());
                }
            });
        };

    player.on('pause', handlePause);
    player.on('play', handlePlay);
    player.on('playing', handlePlaying);
    player.on('seeking', handleSeeking);
    player.on('seeked', handleSeeked);
    player.on('timeupdate', handleTimeUpdate);

        return () => {
            if (player && !player.isDisposed()) {
                player.off('pause', handlePause);
                player.off('play', handlePlay);
                player.off('playing', handlePlaying);
                player.off('seeking', handleSeeking);
                player.off('seeked', handleSeeked);
                player.off('timeupdate', handleTimeUpdate);
            }
            if (seekDebounceRef.current) {
                clearTimeout(seekDebounceRef.current);
                seekDebounceRef.current = null;
            }
        };
    }, [isAdPlaying, isAdRequested, isAudioAd]);

    useEffect(() => {
        const player = playerRef.current;
        if (!player || player.isDisposed()) {
            return;
        }

        const updateDimensions = () => {
            const posterEl = player.el().querySelector('.vjs-poster');
            if (posterEl) {
                const newDimensions = {
                    width: posterEl.clientWidth + 1,
                    height: posterEl.clientHeight + 1,
                };
                console.log('vjs-poster dimensions:', newDimensions);
                setPosterDimensions(newDimensions);
            }
        };

        const resizeObserver = new ResizeObserver(updateDimensions);
        resizeObserver.observe(player.el());

        // Initial update
        updateDimensions();

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    // Safely derive first playlist item and player type
    const firstItem = Array.isArray(playlist) && playlist.length > 0 ? playlist[0] : null;
    const playerType = firstItem?.playerType;
    const PlayerView = isSeries
        ? (playerType === 'single' ? FeaturedAudioView : SeriesPlayerView)
        : BroadcastPlayerView;

    const isSinglePlayer = playerType === 'single';

    // Early render guard if playlist not yet available
    if (!firstItem) {
        return (
            <div style={{ width: '100%', minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif' }}>
                <p style={{ color: '#555', fontSize: 16 }}>{t.loadingPlayer}</p>
            </div>
        );
    }

    // Toggle ad mute/unmute
    const handleToggleAdMute = () => {
        const newMuted = !isAdMuted;
        setIsAdMuted(newMuted);
        if (adsManagerRef.current && typeof adsManagerRef.current.setVolume === 'function') {
            adsManagerRef.current.setVolume(newMuted ? 0 : 1);
        }
        if (playerRef.current && !playerRef.current.isDisposed()) {
            playerRef.current.muted(newMuted);
        }
    };

    const handleSkipAd = () => {
        console.log('⏭️ User clicked Skip Ad');
        console.log('⏭️ playingAudioAdRef.current:', playingAudioAdRef.current);
        console.log('⏭️ isPostrollRef.current:', isPostrollRef.current);
        
        // If we're playing an audio ad through Video.js directly
        if (playingAudioAdRef.current && playerRef.current && !playerRef.current.isDisposed()) {
            console.log('⏭️ Skipping audio ad played through Video.js');
            const player = playerRef.current;
            // Analytics: fire skip for audio ads (IMA's SKIPPED does not fire after we take over playback).
            analytics.trackAdSkip(currentAudioAdRef.current, player.currentTime());
            currentAudioAdRef.current = null;
            const wasPostroll = isPostrollRef.current;
            
            // Capture the next item index BEFORE clearing postroll state
            const capturedNextIndex = nextItemAfterPostrollRef.current;
            console.log('⏭️ Captured nextIndex before cleanup:', capturedNextIndex);
            
            // STOP the current audio ad immediately
            player.pause();
            player.controls(true); // Restore controls immediately
            
            // Reset all ad state
            playingAudioAdRef.current = false;
            isAdPlayingRef.current = false;
            
            // Clean up postroll state if we are skipping
            if (isPostrollRef.current) {
                setIsPostroll(false);
                isPostrollRef.current = false;
                nextItemAfterPostrollRef.current = null;
            }

            // Defer UI cleanup to ensure it overrides any immediate event side effects
            setTimeout(() => {
                setIsAdPlaying(false);
                setIsAdRequested(false);
                isAdRequestedRef.current = false;
                setIsAudioAd(false);
                setAdRemainingTime(null);
                setAdDuration(null);
                setAdSkipTimeOffset(null);
                setAdCompanionPosterUrl(null);
                setAdCompanionClickUrl(null);
            }, 0);
            
            // Handle postroll - advance to next playlist item and play immediately
            if (wasPostroll) {
                console.log('⏭️ Was postroll, advancing to next item and playing');
                setIsPostroll(false);
                isPostrollRef.current = false;
                
                // Get the next playlist item using the captured index
                const playlistItems = player.playlist();
                const nextIndex = capturedNextIndex;
                console.log('⏭️ nextIndex:', nextIndex);
                
                if (nextIndex !== null && nextIndex < playlistItems.length) {
                    const nextItem = playlistItems[nextIndex];
                    console.log('⏭️ Loading next item:', nextItem.name, nextItem.sources);
                    
                    // Load the next item's source directly
                    player.src(nextItem.sources);
                    if (nextItem.poster) {
                        player.poster(nextItem.poster);
                    }
                    
                    // Update playlist index
                    player.playlist.currentItem(nextIndex);
                    player.playlist.autoadvance(isSeries ? 1 : 0);
                    
                    // Clear the stored next item
                    nextItemAfterPostrollRef.current = null;
                    
                    // Play immediately after source is loaded
                    player.one('loadedmetadata', () => {
                        console.log('⏭️ Next item loaded, playing');
                        player.play();
                    });
                } else {
                    console.log('⏭️ No more items in playlist. Cycling back to FIRST item.');
                    nextItemAfterPostrollRef.current = null;
                    
                    // Cycle back to the first item in the playlist
                    const firstIndex = 0;
                    const playlistItems = player.playlist();
                    
                    if (playlistItems && playlistItems.length > 0) {
                        const firstItem = playlistItems[firstIndex];
                        blockPostrollRef.current = true; // Prevent postroll loop on source change
                        
                        player.src(firstItem.sources);
                        if (firstItem.poster) {
                            player.poster(firstItem.poster);
                        }
                        player.playlist.currentItem(firstIndex);
                        player.playlist.autoadvance(isSeries ? 1 : 0);
                        
                        player.one('loadedmetadata', () => {
                            if (isSeries) {
                                // Auto-play for series
                                console.log('🔄 First item loaded, auto-playing for series');
                                player.play();
                            } else {
                                // For featured audio/broadcast: reload at beginning, don't autostart
                                console.log('🔄 First item loaded, pausing at beginning for featured audio');
                                player.currentTime(0);
                                player.pause();
                            }
                        });
                    }
                }
            }
            return;
        }
        
        // If using IMA SDK ads manager
        if (adsManagerRef.current) {
            try {
                adsManagerRef.current.stop();
            } catch (e) {
                console.warn('⚠️ Error stopping ad:', e);
                // Force manual cleanup if stop() fails
                setIsAdPlaying(false);
                isAdPlayingRef.current = false;
                if (playerRef.current && !playerRef.current.isDisposed()) {
                    playerRef.current.play();
                }
            }
        } else {
            setIsAdPlaying(false);
            isAdPlayingRef.current = false;
            if (playerRef.current && !playerRef.current.isDisposed()) {
                playerRef.current.play();
            }
        }
    };

    // Debug: Log render state for ad UI visibility
    console.log('🎨 RENDER: isAudioAd=', isAudioAd, 'isAdPlaying=', isAdPlaying, 'shouldShowAudioAdUI=', isAudioAd && isAdPlaying);
    
    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {isAudioAd && isAdPlaying && (
                <AudioAdUI
                    currentPlaylistItem={currentPlaylistItem}
                    dimensions={posterDimensions}
                    adRemainingTime={adRemainingTime}
                    adDuration={adDuration}
                    isAdMuted={isAdMuted}
                    companionPosterUrl={adCompanionPosterUrl}
                    companionClickUrl={adCompanionClickUrl}
                    companionAltText={adCompanionAltText}
                    onToggleMute={handleToggleAdMute}
                    adSkipTimeOffset={adSkipTimeOffset}
                    onSkipAd={handleSkipAd}
                    onAdClick={() => analytics.trackAdClick(currentAudioAdRef.current)}
                    isSeries={isSeries}
                    isMobile={isMobile}
                />
            )}
            {/* Only render AdCountdownUI directly if it's NOT an audio ad (AudioAdUI renders its own) */}
            {!isAudioAd && isAdPlaying && (
                <AdCountdownUI
                    currentPlaylistItem={currentPlaylistItem}
                    dimensions={posterDimensions}
                    adRemainingTime={adRemainingTime}
                    adDuration={adDuration}
                    adSkipTimeOffset={adSkipTimeOffset}
                    onSkipAd={handleSkipAd}
                    isAdMuted={isAdMuted}
                    onToggleMute={handleToggleAdMute}
                    isSeries={isSeries}
                    isMobile={isMobile}
                />
            )}
            {/* Still image advertisement over paused main video */}
            {!isAdPlaying && !isAdRequested && isContentPaused && pauseAdUrl && (
                <div
                    style={{
                        position: 'absolute',
                        top: isSeries ? (isSinglePlayer ? 0 : 25) : 20,
                        left: isSeries ? (isMobile ? 10 : (isSinglePlayer ? 0 : 25)) : 20,
                        width: posterDimensions ? `${posterDimensions.width}px` : '100%',
                        height: posterDimensions ? `${posterDimensions.height}px` : '100%',
                        zIndex: 101,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <a
                        href={pauseAdClickUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t.becomeMonthlyCompanionTooltip}
                        style={{
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'auto',
                            position: 'relative',
                        }}
                    >
                        <img
                            src={pauseAdUrl}
                            alt="Advertisement"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                            }}
                        />
                    </a>
                </div>
            )}
            <div ref={adContainerRef} style={{
                position: 'absolute',
                top: isSeries ? (isSinglePlayer ? 0 : 25) : 20,
                left: isSeries ? (isMobile ? 10 : (isSinglePlayer ? 0 : 25)) : 20,
                width: posterDimensions ? `${posterDimensions.width}px` : '100%',
                height: posterDimensions ? `${posterDimensions.height}px` : '100%',
                pointerEvents: isAdPlaying ? 'auto' : 'none',
                zIndex: isAdPlaying ? 99 : 'auto',
            }} />
            <div className={!isAdPlaying && !isAdRequested && isContentPaused && pauseAdUrl ? 'vjs-pause-ad-active' : ''}>
                <PlayerView
                    playlist={playlist}
                    currentPlaylistItem={currentPlaylistItem}
                    videoRef={handlePlayerMount}
                    playlistContainerRef={playlistContainerRef}
                    isMobile={isMobile}
                    isAdPlaying={isAdPlaying}
                    textColor={textColor}
                />
            </div>
        </div>
    );
};

export default NewVideoPlayer;
